import { CodeHighlight } from '@mantine/code-highlight';
import { Button, Group, ScrollArea, SegmentedControl, Select, Text, useTree } from '@mantine/core';
import { IconPlayerPlay } from '@tabler/icons-react';
import { useLocalStorage } from '@mantine/hooks';
import { memo, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_SNIPPET_CLIENT,
  SNIPPET_CLIENT_OPTIONS,
  getServerUrl,
  getSnippetLanguage,
  printSnippet,
} from './code-snippets';
import { CopyIconButton, ExplorerPanel } from './ExplorerParts';
import JsonTree from './JsonTree';
import { getExampleOverrides } from './example-overrides';
import { generateModuleExample } from './module-example';
import type { ModuleIndex } from './module-filter';
import type { ModuleId } from './module-labels';
import { getRequestBodySchema } from './openapi-utils';
import type { SpecOperation } from './spec-tree';

type ModuleExampleSnippetProps = {
  spec: Record<string, unknown>;
  moduleIndex: ModuleIndex;
  operations: SpecOperation[];
  selectedModules: ReadonlySet<ModuleId>;
  /** Called with the operation and the example currently shown */
  onExecuteRequest: (operation: SpecOperation, body: unknown) => void;
};

type SnippetMode = 'json' | 'code';

const operationKey = (operation: SpecOperation) => `${operation.method} ${operation.path}`;

function ModuleExampleSnippet({
  spec,
  moduleIndex,
  operations,
  selectedModules,
  onExecuteRequest,
}: ModuleExampleSnippetProps) {
  const withBody = useMemo(
    () => operations.filter((operation) => getRequestBodySchema(spec, operation.path, operation.method)),
    [spec, operations],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [mode, setMode] = useState<SnippetMode>('json');
  const [clientId, setClientId] = useLocalStorage({
    key: 'module-explorer-snippet-client',
    defaultValue: DEFAULT_SNIPPET_CLIENT,
  });

  const operation = withBody.find((op) => operationKey(op) === selectedKey) ?? withBody[0];

  const example = useMemo(() => {
    const schema = operation && getRequestBodySchema(spec, operation.path, operation.method);
    return schema
      ? generateModuleExample(spec, moduleIndex, schema, selectedModules, getExampleOverrides(spec))
      : undefined;
  }, [spec, moduleIndex, operation, selectedModules]);

  const json = useMemo(() => JSON.stringify(example, null, 2), [example]);

  // Only print code while the code view is showing
  const code = useMemo(() => {
    if (mode !== 'code' || !operation || example === undefined) return undefined;
    const url = `${getServerUrl(spec)}${operation.path}`;
    return printSnippet(clientId, { method: operation.method, url, body: example });
  }, [mode, clientId, spec, operation, example]);

  const tree = useTree();
  const jsonViewport = useRef<HTMLDivElement>(null);

  if (!operation || example === undefined) return null;

  return (
    <ExplorerPanel
      title="Example request"
      description={
        selectedModules.size === 0
          ? 'select modules to fill in the fields they use'
          : `filled in for ${selectedModules.size} selected ${selectedModules.size === 1 ? 'module' : 'modules'}`
      }
      flex={1}
      mih={0}
      actions={
        <>
          <Button
            size="compact-xs"
            variant="light"
            leftSection={<IconPlayerPlay size={14} />}
            onClick={() => onExecuteRequest(operation, example)}
          >
            Execute request
          </Button>
          <CopyIconButton
            value={mode === 'code' ? (code ?? '') : json}
            label={mode === 'code' ? 'Copy code' : 'Copy JSON'}
          />
        </>
      }
      toolbar={
        <Group gap="md" justify="space-between">
          <Group gap="xs">
            <SegmentedControl
              size="xs"
              data={[
                { value: 'json', label: 'JSON' },
                { value: 'code', label: 'Code' },
              ]}
              value={mode}
              onChange={(value) => setMode(value as SnippetMode)}
            />
            {mode === 'code' && (
              <Select
                size="xs"
                aria-label="Client"
                data={SNIPPET_CLIENT_OPTIONS}
                value={clientId}
                onChange={(value) => value && setClientId(value)}
                allowDeselect={false}
                searchable
                maxDropdownHeight={320}
              />
            )}
            {withBody.length > 1 && (
              <Select
                size="xs"
                data={withBody.map((op) => ({
                  value: operationKey(op),
                  label: `${op.method.toUpperCase()} ${op.path}`,
                }))}
                value={operationKey(operation)}
                onChange={setSelectedKey}
                allowDeselect={false}
              />
            )}
          </Group>
          {mode === 'json' && (
            <Group gap={4}>
              <Button variant="subtle" size="compact-xs" onClick={() => tree.expandAllNodes()}>
                Expand all
              </Button>
              <Button variant="subtle" size="compact-xs" onClick={() => tree.collapseAllNodes()}>
                Collapse all
              </Button>
            </Group>
          )}
        </Group>
      }
    >
      {mode === 'code' ? (
        <ScrollArea flex={1}>
          {code === undefined ? (
            <Text c="dimmed" size="sm" p="md">
              No snippet is available for this client.
            </Text>
          ) : (
            <CodeHighlight code={code} language={getSnippetLanguage(clientId)} withCopyButton={false} radius={0} />
          )}
        </ScrollArea>
      ) : (
        <ScrollArea flex={1} p="xs" viewportRef={jsonViewport}>
          <JsonTree value={example} tree={tree} scrollRef={jsonViewport} />
        </ScrollArea>
      )}
    </ExplorerPanel>
  );
}

// Memoised so selecting nodes in the schema tree beside it does not re-render the example
export default memo(ModuleExampleSnippet);
